import { useState } from "react";
import { useNavigate } from "react-router-dom";

import dashboardMockup from "../../../assets/project updates/Construction Dashboard iPhone Mockup.png";
import paintImage from "../../../assets/project updates/paint_work_completed.png";
import plasterImage from "../../../assets/project updates/plaster_work_completed.png";
import structureImage from "../../../assets/project updates/structure_work_completed.png";
import updateMockup from "../../../assets/project updates/updateMockup.png";
import {
  ArrowRight,
  Building2,
  Clock3,
  FileText,
  Images,
  LogIn,
  PhoneCall,
} from "lucide-react";
import { getSession, notifyAuthChanged } from "../../../app/auth";
import Login from "../pages/Login";
import "./ClientPortalShowcase.css";

const features = [
  {
    Icon: Clock3,
    title: "Project Updates",
    text: "Get regular progress updates from your project team.",
    tone: "orange",
  },
  {
    Icon: Images,
    title: "Site Photos",
    text: "View latest site photos and progress gallery.",
    tone: "green",
  },
  {
    Icon: FileText,
    title: "Documents",
    text: "Access important drawings, approvals and documents.",
    tone: "blue",
  },
  {
    Icon: Building2,
    title: "Project Details",
    text: "Keep your key project information together in one place.",
    tone: "orange",
  },
];

// Illustrative content only. This public section never fetches client data.
const updates = [
  {
    date: "05 Oct 2026",
    title: "Paint Work Completed",
    text: "Exterior painting completed with final finishing touches.",
    image: paintImage,
  },
  {
    date: "28 Sep 2026",
    title: "Plaster Work Completed",
    text: "Internal and external plastering completed successfully.",
    image: plasterImage,
  },
  {
    date: "20 Sep 2026",
    title: "Structure Work Completed",
    text: "RCC structural work completed as per approved design.",
    image: structureImage,
  },
];

export default function ClientPortalShowcase({ onRequestCallback }) {
  const [showLogin, setShowLogin] = useState(false);
  const navigate = useNavigate();

  const openPortal = () => {
    if (getSession()) {
      navigate("/dashboard");
    } else {
      notifyAuthChanged();
      setShowLogin(true);
    }
  };

  return (
    <>
      <section
        id="project-updates"
        className="portal-showcase"
        aria-labelledby="portal-showcase-title"
      >
        <div className="ps-hero">
          <div className="ps-hero-inner">
            <div className="ps-intro">
              <p className="ps-eyebrow">Stay connected, always informed</p>
              <h2 id="portal-showcase-title">
                Regular
                <br />
                <span>Project Updates</span>
                <br />
                on Our
                <br />
                Client Portal
              </h2>
              <div className="ps-rule" />
              <p className="ps-copy">
                Get regular updates, progress photos, important documents and
                key project details – all in one place, through the Spirent
                India Client Portal.
              </p>
              <div className="ps-actions">
                <button
                  type="button"
                  className="ps-button"
                  onClick={openPortal}
                >
                  <LogIn aria-hidden="true" size={23} /> Access Client Portal
                </button>
                <button
                  type="button"
                  className="ps-button ps-button-dark"
                  onClick={onRequestCallback}
                >
                  <PhoneCall aria-hidden="true" size={23} /> Request Callback
                </button>
              </div>
            </div>

            <div className="ps-dashboard-wrap ps-phone-preview">
              <img
                className="ps-phone-mockup"
                src={dashboardMockup}
                alt="Client portal demo on an iPhone showing project progress and recent updates"
                loading="lazy"
                decoding="async"
              />
              <img
                className="ps-phone-mockup"
                src={updateMockup}
                alt="Client portal demo on an iPhone showing project progress and recent updates"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>

        <div className="ps-features">
          <p className="ps-eyebrow">Everything you need</p>
          <h2>Complete Project Information</h2>
          <p className="ps-feature-intro">
            Access all important details about your project through a simple and
            secure client portal.
          </p>
          <div className="ps-feature-grid">
            {features.map(({ Icon, title, text, tone }) => (
              <div className="ps-feature" key={title}>
                <span className={`ps-feature-icon ps-tone-${tone}`}>
                  <Icon size={35} strokeWidth={1.8} aria-hidden="true" />
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="ps-updates">
          <div className="ps-updates-intro">
            <p className="ps-eyebrow">Regular progress updates</p>
            <h2>
              Recent Project
              <br />
              Updates
            </h2>
            <p className="ps-copy">
              See the latest construction progress, photos and activities shared
              by your project team.
            </p>
            {/* <button type="button" className="ps-button">
              View All Updates <ArrowRight size={22} aria-hidden="true" />
            </button> */}
          </div>
          <div className="ps-timeline">
            <p className="ps-demo-label">Demo preview · Sample updates</p>

            <ol>
              {updates.map((update) => (
                <li key={update.date}>
                  <time>{update.date}</time>

                  <div className="ps-update-photo">
                    <img src={update.image} alt={update.title} loading="lazy" />
                  </div>

                  <div className="ps-update-copy">
                    <h3>{update.title}</h3>
                    <p>{update.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="ps-transparency">
          <div className="ps-transparency-copy">
            <p className="ps-eyebrow">Transparent communication</p>
            <h2>
              A Stronger Partnership
              <br />
              Through Transparency
            </h2>
            <p className="ps-copy">
              At Spirent India, we believe in keeping our clients informed at
              every stage. Our client portal ensures you are always connected to
              your project, no matter where you are.
            </p>
            <button type="button" className="ps-button" onClick={openPortal}>
              <LogIn size={23} aria-hidden="true" /> Login to Client Portal{" "}
              <ArrowRight size={22} aria-hidden="true" />
            </button>
          </div>
          {/* <img
            className="ps-transparency-house"
            src={houseImage}
            alt="Modern residential home design"
            loading="lazy"
          /> */}
        </div>
      </section>
      {showLogin && (
        <Login
          isPopup
          onClose={() => setShowLogin(false)}
          setAuth={notifyAuthChanged}
        />
      )}
    </>
  );
}
